// The timer window events inside POPPED-OUT pages (PopupLayout).  Layout
// handles them itself in the main window; /popup/* renders PopupLayout, so
// without this bridge "Setup & Start", "+ Add Time", "▶ Resume" and the
// one-click "Start Timer" buttons inside a popped-out TimeEntries /
// MatterDetail / Dashboard window would have no listener and do nothing.
//
//   'open-time-entry-popup' — a timer FORM.  Desktop app: a pop-out timer
//     window per request (a resume under 'timer-resume-<id>', so a second
//     click focuses it).  A browser cannot open windows, so it is inert there.
//
//   'start-timer' {matter_id?, matter_title?, billing_type?} — a one-click,
//     HEADLESS start (docs/specs/timers-one-click-spec.md): the slot is
//     created here through lib/timerStore (the one-running rule pauses my
//     other timers), then its form is opened with openTimerForm(key) — the
//     existing ?slot=&pk= route, so the person sees it, and a reload of that
//     window adopts the slot instead of starting a second timer.  Same
//     can_post_time gate (a toast, nothing created) and the same 1 s
//     double-fire guard as every other one-click start
//     (components/TimersPanel.js).  In a browser the start still happens,
//     with the "Timer started" toast in place of a window.
//
// forwardToLegacy: true makes the hook inert (a shell that handles these
// events itself).

import { useEffect, useRef } from 'react'
import { safeOpenPopupWindow } from '../services/token'
import { timerWindowOptions } from '../utils/desktopVersion'
import { tagRouteWithPopupKey } from '../lib/timerSlots'
import { awaitHeldPauses, openFormAfterHeldPause, openTimerForm } from '../lib/timerWindows'
import timerStore, { MESSAGES } from '../lib/timerStore'
import { hasPermission } from '../lib/navItems'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'

// ── One-click helpers ─────────────────────────────────────────────────────
// Shared by Layout (the start-timer event, the tray, Ctrl/Cmd+Shift+T), the
// Timers chip and panel (components/TimersPanel.js) and the bridge below, so
// every one-click start has the same can_post_time gate, the same 1 s
// double-fire guard and the same messages.  This module has no API client
// dependency on purpose: every action here is local (lib/timerStore).

// One click = one timer.  The chip, the panel's "+ Start timer", the
// start-timer event, the tray and the shortcut can each fire twice for one
// gesture (a double click; Ctrl+Shift+T arrives through the global shortcut
// AND the menu accelerator) — a second start would pause the first.
export const ONE_CLICK_GUARD_MS = 1000

export function createOneClickGuard ({ ms = ONE_CLICK_GUARD_MS, now = () => Date.now() } = {}) {
  let last = -Infinity
  return {
    tryFire () {
      const t = now()
      if (t - last < ms && t >= last) return false
      last = t
      return true
    },
    reset () { last = -Infinity }
  }
}

// One guard per window (module state = one per JS realm), shared by every
// one-click entry point in it.
export const oneClickGuard = createOneClickGuard()

// ▶/⏸ on one timer (the chip, a panel row) applies ONCE per double click.
// lib/timerStore's toggle flips whatever it reads, and the first click's work
// is finished long before the second click of a double click arrives, so
// without this the second click undid the first: a paused timer clicked twice
// on ▶ ended paused, and a double click on '⏱ Start timer' paused the timer it
// had just started (its second click lands on the chip's new ⏸).  The person
// believes the clock runs while it does not — unbilled time.  Per timer key:
// a toggle within TOGGLE_GUARD_MS of the last action on that timer (a toggle,
// or the one-click start that created it — hold(), ONE_CLICK_GUARD_MS) is
// dropped.  Other timers are not held.
export const TOGGLE_GUARD_MS = 500

export function createKeyGuard ({ now = () => Date.now() } = {}) {
  const last = new Map() // key → { at, span }
  const held = (key, t) => {
    const h = last.get(key)
    return !!h && t >= h.at && t - h.at < h.span
  }
  const prune = (t) => {
    if (last.size <= 64) return
    for (const [k, h] of last) if (!(t >= h.at && t - h.at < h.span)) last.delete(k)
  }
  return {
    tryFire (key, span = TOGGLE_GUARD_MS) {
      if (!key) return true
      const t = now()
      if (held(key, t)) return false
      last.set(key, { at: t, span })
      prune(t)
      return true
    },
    hold (key, span = ONE_CLICK_GUARD_MS) {
      if (!key) return
      const t = now()
      const h = last.get(key)
      // Never shorten a hold already running.
      if (h && t >= h.at && h.at + h.span >= t + span) return
      last.set(key, { at: t, span })
      prune(t)
    },
    reset () { last.clear() }
  }
}

export const toggleGuard = createKeyGuard()

// The second (third…) click of a multi-click: MouseEvent.detail counts clicks
// at one spot within the OS double-click time, whichever element is under the
// pointer — so it also catches the '⏱ Start timer' → ⏸ case.  Keyboard
// activation has detail 0.
export const isRepeatClick = (ev) => !!(ev && typeof ev.detail === 'number' && ev.detail > 1)

// The store's confirm callback, through the app's own dialog (never
// window.confirm — it throws in Electron).
export const confirmVia = (ask) => async (req) => {
  if (typeof ask !== 'function' || !req) return false
  const yes = await ask({ type: 'confirm', danger: true, message: req.message, confirmLabel: req.confirmLabel })
  return yes === true
}

const isDesktop = (electronAPI) => !!(electronAPI && typeof electronAPI.openPopupWindow === 'function')
const windowElectron = () => {
  try { return typeof window !== 'undefined' ? window.electronAPI : undefined } catch { return undefined }
}

// Show what a store result means.  A storage failure is persistent (time is
// not being recorded); a Cancel is silent; old-bundle windows that were sent
// a command are watched for their ack ("Pause "<label>" in its window").
// `warn: false` leaves other refusals to the caller (the open panel shows
// them inline instead of as a toast).  `started`: true shows MESSAGES.started,
// a string shows that line instead.
export function reportStoreResult (r, { toast, onStorage, started = false, warn = true } = {}) {
  if (!r) return
  const t = toast || {}
  if (!r.ok) {
    if (r.reason === 'storage') {
      if (typeof t.error === 'function') t.error(MESSAGES.storage, { duration: 0 })
      if (typeof onStorage === 'function') onStorage()
      return
    }
    if (r.reason === 'cancelled' || r.reason === 'guarded') return
    if (warn && r.message && typeof t.warn === 'function') t.warn(r.message)
    return
  }
  if (Array.isArray(r.failed) && r.failed.length) {
    if (typeof t.error === 'function') t.error(MESSAGES.storage, { duration: 0 })
    if (typeof onStorage === 'function') onStorage()
  }
  if (started && typeof t.success === 'function') t.success(typeof started === 'string' ? started : MESSAGES.started)
  if (Array.isArray(r.held) && r.held.length) {
    awaitHeldPauses(r.held, {
      onSilence: ({ message }) => { if (typeof t.warn === 'function') t.warn(message) }
    })
  }
}

// A row whose timer went since it was drawn: say why when the store knows
// (store.removalNote — matter.blocked removed a resume timer), else the
// generic MESSAGES.missing ("saved or discarded in another window").
export function goneMessageFor (store, row) {
  let note = null
  try {
    note = store && row && typeof store.removalNote === 'function'
      ? store.removalNote(row.key, { tid: row.slot ? row.slot.tid : undefined })
      : null
  } catch { note = null }
  return (note && note.message) || MESSAGES.missing
}
const withGoneMessage = (store, row, r) =>
  (r && !r.ok && r.reason === 'missing' ? { ...r, message: goneMessageFor(store, row) } : r)

// The fields a one-click start may carry: the matter only.  Never a rate —
// a prefilled rate would be sent with the entry and override the server's
// getEffectiveRate (the attorney's own rate on that matter).
export function oneClickFields (detail) {
  const d = detail && typeof detail === 'object' ? detail : {}
  const out = {}
  if (d.matter_id != null && d.matter_id !== '') {
    out.matter_id = String(d.matter_id)
    if (d.matter_title != null) out.matter_title = String(d.matter_title)
    if (d.billing_type != null) out.billing_type = String(d.billing_type)
  }
  return out
}

// A headless one-click start: the chip, the panel's "+ Start timer", the
// start-timer event, the tray's Start Timer.  Without can_post_time: a toast
// and nothing is created.  `quiet` skips the "Timer started" toast (a pop-out
// page opens the new timer's form instead, which is feedback enough).  A start
// that already carries its matter (the matter page) says MESSAGES.
// startedOnMatter instead of "pick the matter" — neither names the matter.
export async function startOneClickTimer ({
  store = timerStore, user, detail, toast, ask, guard = oneClickGuard, keyGuard = toggleGuard, quiet = false, onStorage, warn = true
} = {}) {
  if (!hasPermission(user, 'can_post_time')) {
    if (toast && typeof toast.warn === 'function') toast.warn(MESSAGES.noPermission)
    return { ok: false, reason: 'no-permission', message: MESSAGES.noPermission }
  }
  if (guard && !guard.tryFire()) return { ok: false, reason: 'guarded' }
  const fields = oneClickFields(detail)
  let r
  try {
    r = await store.startNewTimer({ fields, confirm: confirmVia(ask) })
  } catch {
    r = { ok: false, reason: 'storage', message: MESSAGES.storage }
  }
  // The new timer's ▶/⏸ ignores the rest of this gesture (see toggleGuard).
  if (keyGuard && r && r.ok && r.key) keyGuard.hold(r.key, ONE_CLICK_GUARD_MS)
  const started = quiet ? false : (fields.matter_id ? MESSAGES.startedOnMatter : true)
  reportStoreResult(r, { toast, onStorage, started, warn })
  return r
}

// ── ⏸ opens that timer's form (James, 2026-10-15) ───────────────────────
// "When you hit pause, make it so the timer entry window pops up."  Only for
// a pause the PERSON asked for, in the window they asked in — the chip's ⏸, a
// panel row's ⏸ (openForm below), Ctrl/Cmd+Shift+T when it pauses (Layout),
// the floating bar's ⏯ (pages/TimerBar.js) — and only once that pause really
// paused the timer: never after a refusal, a no-op on a timer already paused,
// a resume or a start.  Automatic pauses (the one-running rule, the
// reconciler, sign-out, matter.blocked, claimForSave, adoption, a command
// another window applies) never come through here.  The form opens the way
// "Fill in & save" opens it (openTimerFormFor) — except that the browser's
// in-page opener is told it follows a pause, and Layout then keeps a form
// holding unsaved work (Layout.js PAUSE_FORM_KEPT).  The pause stands whatever
// the opening does, and a failure is reported the usual way.

// The key a ⏸ actually paused, or null.  `r` = a pause result (store.pause /
// store.toggle); `showedTicking` = the button showed ⏸.
export function pausedKeyOf (r, showedTicking) {
  if (!showedTicking || !r || !r.ok) return null
  return r.changed === true || r.viaCommand ? (r.key || null) : null
}

// Open the form of timer `key`, which this click has just paused
// (`pauseResult`).  The row is re-read: a timer adopted by the pause is v2
// now.  A timer an old-bundle window holds (paused by command) is focused in
// its window (openFormAfterHeldPause — it waits for the pause's ack when that
// window recorded no popupKey, so it never answers in that window's place).
export async function openFormAfterPause (key, pauseResult, {
  store = timerStore, ask, toast, onOpenInPage, electronAPI = windowElectron(), onStorage, warn = true
} = {}) {
  if (!key) return { ok: false, reason: 'missing' }
  try {
    let row = null
    try { row = store.getTimer(key) } catch { row = null }
    const cmd = pauseResult && pauseResult.viaCommand
    if (row && row.cls === 'v1-held' && cmd && isDesktop(electronAPI) && !(row.owner && row.owner.pk)) {
      openFormAfterHeldPause(key, cmd, { electronAPI })
        .catch((err) => console.error('pause: could not bring the timer window forward', err))
      return { ok: true, how: { how: 'after-ack' } }
    }
    // The in-page opener is told this open follows a pause: Layout keeps a
    // form holding unsaved work instead of swapping it (PAUSE_FORM_KEPT).
    const inPage = typeof onOpenInPage === 'function' ? (k) => onOpenInPage(k, { afterPause: true }) : onOpenInPage
    return await openTimerFormFor(row || { key }, { store, ask, toast, onOpenInPage: inPage, electronAPI, onStorage, warn })
  } catch (err) {
    console.error('pause: could not open the timer form', err)
    return { ok: false, reason: 'open-failed' }
  }
}

// ▶/⏸ on one timer row (the chip's current timer, a panel row).
//   • `row` is the row the button was RENDERED from: ▶ asks to resume, ⏸ to
//     pause.  When the timer is already in that state (another window or tab
//     got there first), nothing happens — the store's toggle would flip it
//     back (spec: "resume on one that is not paused … do nothing").
//   • The rest of a double click is dropped (isRepeatClick, toggleGuard).
//   • A timer written by old code is adopted first (store.toggle, through
//     confirm where an old tab may run it); an old-bundle window's timer gets
//     the command, and its silence is reported (reportStoreResult).
//   • openForm ({ onOpenInPage, electronAPI }): a ⏸ that paused it then opens
//     its form (openFormAfterPause); the result carries `form`.
export async function toggleTimerRow (row, {
  store = timerStore, ask, toast, onStorage, warn = true, guard = toggleGuard, event, openForm = null
} = {}) {
  if (!row || !row.key) return { ok: false, reason: 'missing' }
  if (isRepeatClick(event)) return { ok: false, reason: 'guarded' }
  let fresh = null
  try { fresh = store.getTimer(row.key) } catch { fresh = null }
  if (fresh && typeof row.ticking === 'boolean' && !!fresh.ticking !== row.ticking) {
    return { ok: true, key: row.key, changed: false }
  }
  if (guard && !guard.tryFire(row.key)) return { ok: false, reason: 'guarded' }
  let r
  try {
    r = await store.toggle(row.key, { confirm: confirmVia(ask) })
  } catch {
    r = { ok: false, reason: 'storage', message: MESSAGES.storage }
  }
  r = withGoneMessage(store, row, r)
  reportStoreResult(r, { toast, onStorage, warn })
  const paused = openForm ? pausedKeyOf(r, row.ticking === true) : null
  if (!paused) return r
  const form = await openFormAfterPause(paused, r, { store, ask, toast, onStorage, warn, ...openForm })
  return { ...r, form }
}

// Ctrl/Cmd+Shift+T (firm decision 2026-10-13): pause my running timer,
// otherwise start a new one.  Same gate and guard as a one-click start.
// openForm ({ onOpenInPage, electronAPI }): when it paused, the first timer
// it really paused opens its form (openFormAfterPause); a start opens none.
export async function toggleOneClickTimer ({
  store = timerStore, user, toast, ask, guard = oneClickGuard, onStorage, openForm = null
} = {}) {
  if (!hasPermission(user, 'can_post_time')) {
    if (toast && typeof toast.warn === 'function') toast.warn(MESSAGES.noPermission)
    return { ok: false, reason: 'no-permission', message: MESSAGES.noPermission }
  }
  if (guard && !guard.tryFire()) return { ok: false, reason: 'guarded' }
  let r
  try {
    r = await store.pauseOrStart({ confirm: confirmVia(ask) })
  } catch {
    r = { ok: false, reason: 'storage', message: MESSAGES.storage }
  }
  reportStoreResult(r, { toast, onStorage, started: !!(r && r.ok && r.action === 'started') })
  if (!openForm || !r || !r.ok || r.action !== 'paused') return r
  const hit = (Array.isArray(r.results) ? r.results : [r]).find(x => pausedKeyOf(x, true))
  if (!hit) return r
  const form = await openFormAfterPause(hit.key, hit, { store, ask, toast, onStorage, ...openForm })
  return { ...r, form }
}

// "Fill in & save" for one timer row.
//   • An old-bundle window holds it (v1-held): bring that window forward
//     (openTimerForm focuses it by its popupKey, else sends 'focus').
//   • Written by old code but nothing can hold it (adopt-silently), or an
//     old tab might (v1-unknown → the adopt confirm): adopt it first, so the
//     form always opens on a slot the new code owns.
//   • Desktop: openTimerForm — focus the window already on it, else open one
//     on the slot.  Browser: the in-page form (onOpenInPage(key)).
export async function openTimerFormFor (row, {
  store = timerStore, ask, toast, onOpenInPage, electronAPI = windowElectron(), onStorage, warn = true
} = {}) {
  if (!row || !row.key) return { ok: false, reason: 'missing' }
  const desktop = isDesktop(electronAPI)
  if (row.cls === 'v1-held') {
    if (desktop) {
      const how = await openTimerForm(row.key, { electronAPI })
      return { ok: true, how }
    }
    if (warn && toast && typeof toast.warn === 'function') toast.warn(MESSAGES.held)
    return { ok: false, reason: 'held', message: MESSAGES.held }
  }
  if (row.cls && row.cls !== 'v2') {
    const a = withGoneMessage(store, row, await store.adoptV1(row.key, { confirm: confirmVia(ask) }))
    if (!a.ok) {
      reportStoreResult(a, { toast, onStorage, warn })
      return a
    }
  }
  if (desktop) {
    const how = await openTimerForm(row.key, { electronAPI })
    if (how && how.how === 'missing') {
      const message = goneMessageFor(store, row)
      if (warn && toast && typeof toast.warn === 'function') toast.warn(message)
      return { ok: false, reason: 'missing', message, how }
    }
    return { ok: true, how }
  }
  // Browser: the in-page form on a timer that has gone would open blank and
  // silent — say why instead, as the desktop path does.
  let present = true
  try { present = typeof store.getTimer !== 'function' || !!store.getTimer(row.key) } catch { present = true }
  if (!present) {
    const message = goneMessageFor(store, row)
    if (warn && toast && typeof toast.warn === 'function') toast.warn(message)
    return { ok: false, reason: 'missing', message }
  }
  if (typeof onOpenInPage === 'function') onOpenInPage(row.key)
  return { ok: true, how: { how: 'in-page' } }
}


export default function useTimerEventBridge({ forwardToLegacy = false, store = timerStore } = {}) {
  const auth = useAuth()
  const toast = useToast()
  const ask = useAsk()
  // The listeners register once; they read the live user / toast / ask here.
  const liveRef = useRef(null)
  liveRef.current = { user: auth ? auth.user : null, toast, ask, store }

  useEffect(() => {
    if (forwardToLegacy) return undefined

    const onStart = async (e) => {
      const live = liveRef.current || {}
      const desktop = !!(window.electronAPI && typeof window.electronAPI.openPopupWindow === 'function')
      const r = await startOneClickTimer({
        store: live.store,
        user: live.user,
        detail: e && e.detail,
        toast: live.toast,
        ask: live.ask,
        // A window opens on the new timer — that is the feedback.
        quiet: desktop
      })
      if (!r || !r.ok || !r.key || !desktop) return
      try {
        const how = await openTimerForm(r.key)
        if (how && how.how === 'missing' && live.toast && typeof live.toast.warn === 'function') live.toast.warn(MESSAGES.missing)
      } catch (err) {
        console.error('start-timer: could not open the timer window', err)
      }
    }
    window.addEventListener('start-timer', onStart)

    // Only the desktop app can open a timer window.
    let onOpen = null
    if (window.electronAPI?.openPopupWindow) {
      onOpen = (e) => {
        const resume = e.detail?.resume_entry
        const uniq = (key) => key + '-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8)
        // safeOpenPopupWindow: token migration + #auth hash (same as Layout's
        // spawnTimerWindow); timerWindowOptions(): shared sizes/mins + the
        // version-gated 'timer' boundsKey.
        //
        // Desktop 1.0.13 timer bar (James, 2026-10-01): tagRouteWithPopupKey
        // adds ?pk=<the key this window is opened under>, exactly as Layout's
        // spawnTimerWindow does.  The window records that key for the floating
        // bar, whose ↗ then focuses THIS window and whose "reopen" lands on it
        // rather than opening a second window on the same timer.  This path
        // (Setup & Start / + Add Time inside a popped-out page) once skipped it:
        // ↗ did nothing and ⏯ on a hung window opened a duplicate (seam audit).
        // Harmless on 1.0.12 and in a browser: PopupTimer only passes it along.
        try {
          if (resume?.id) {
            const key = `timer-resume-${resume.id}`
            safeOpenPopupWindow(
              key,
              tagRouteWithPopupKey(`/popup/timer/resume/${resume.id}`, key),
              timerWindowOptions()
            )
          } else {
            const params = new URLSearchParams()
            if (e.detail?.matter_id) params.set('matter_id', String(e.detail.matter_id))
            if (e.detail?.rate)      params.set('rate', String(e.detail.rate))
            const qs = params.toString()
            const key = uniq('timer')
            safeOpenPopupWindow(
              key,
              tagRouteWithPopupKey(`/popup/timer/new${qs ? `?${qs}` : ''}`, key),
              timerWindowOptions()
            )
          }
        } catch (err) {
          console.error('open-time-entry-popup: could not open the timer window', err)
        }
      }
      window.addEventListener('open-time-entry-popup', onOpen)
    }

    return () => {
      window.removeEventListener('start-timer', onStart)
      if (onOpen) window.removeEventListener('open-time-entry-popup', onOpen)
    }
  }, [forwardToLegacy])
}
